import {captureException} from '@sentry/browser';
import React, {useCallback, useMemo} from 'react';
import isEqual from 'react-fast-compare';

import * as Analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import DocumentEditorContext from 'notebook/documents/DocumentEditorContext';
import {DocumentNoteProvider} from 'notebook/documents/DocumentNoteProvider';
import DocumentStore from 'notebook/documents/DocumentStore';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import {TableCellEditor, TableCellEditorProps} from 'notebook/tables/core-ui/TableCellEditor';
import useMultiWindowEditing from 'notebook/tables/core-ui/useMultiWindowEditing';
import {setCellEditorPosition} from 'notebook/tables/spreadsheets/SpreadsheetActions';
import * as SpreadsheetEvalUtil from 'notebook/tables/spreadsheets/SpreadsheetEvalUtil';
import SpreadsheetStore from 'notebook/tables/spreadsheets/SpreadsheetStore';
import {
  HydratedTableContext,
  HydratedTableProvider,
} from 'notebook/tables/store/contexts/HydratedTableContext';
import {RowModelProvider} from 'notebook/tables/store/contexts/RowModelContext';
import {TableModelProvider} from 'notebook/tables/store/contexts/TableModelContext';
import {useCrossStepFormulaBox} from 'notebook/tables/store/hooks/useCrossStepFormulaBox';
import {getWorksheetIdFromNamespace} from 'notebook/tables/util/WorksheetTableNamespaceUtil';
import useStoreSelector from 'ui-library/hooks/useStoreSelector';

import {TableNoteContext} from '../store/contexts/TableNoteContext';
import {getEvaluatedTable} from '../store/selectors/TableSelectors';
import {TABLE_EDITABLE_DEFAULT_PROPS} from './TableEditableDefaultProps';
import {TableEditableProps} from './TableEditableProps';

interface CrossStepFormulaBoxProps {
  stepName: string;
  stepIdBeingEdited: string;
  stepIdBeingDisplayed: string;
  tableName: string;
  index: TableIndex;
  tableNamespace: string;
  onReturnToStep: () => void;
}

function CrossStepFormulaBoxInner({
  stepName,
  stepIdBeingEdited,
  stepIdBeingDisplayed,
  tableName,
  index,
  tableNamespace,
  onReturnToStep,
}: CrossStepFormulaBoxProps): JSX.Element {
  const {cid, cellError, lastIndex, updateCell} = useCrossStepFormulaBox(index);
  const formulaBoxRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const worksheetId = getWorksheetIdFromNamespace(tableNamespace);
    if (!worksheetId) {
      return;
    }

    Analytics.trackEvent(ELN_ANALYTICS_ACTIONS.OPENED_CROSS_STEP_EDITOR, {
      action_origin: ActionOrigin.WORKSHEET,
      worksheet_id: worksheetId,
      step_count: DocumentStore.getRelatedDocumentIds(stepIdBeingEdited).length,
    });
  }, [tableNamespace, stepIdBeingEdited]);

  useMultiWindowEditing({
    tableNamespace,
    fileId: stepIdBeingEdited,
    tableName,
    documentName: stepName,
    domNode: formulaBoxRef.current!,
    multiStartIndex: null,
    multiEndIndex: null,
    hasFormulasDisabled: false,
    multiActive: false,
    multiEditing: true,
    onEditFinalize: async () => {
      const {formula, typedValue} = SpreadsheetStore.getState(tableNamespace, tableName, stepIdBeingEdited);
      await updateCell({text: formula, typedValue});
      finishEditing();
    },
  });

  function finishEditing(): void {
    SpreadsheetEvalUtil.setMultiEditingForTable(
      {tableName, documentName: stepName},
      tableNamespace,
      stepIdBeingEdited,
      {
        index: null,
        editing: false,
        text: '',
        typedValue: null,
        selection: null,
      }
    );
    setCellEditorPosition(null);
  }

  async function onCellEnter(e: {text: string; cellData: Partial<Cell>}): Promise<void> {
    await updateCell({...e.cellData, text: e.text});
    finishEditing();
    onReturnToStep();
  }

  const {getNote} = TableNoteContext.useContext();
  const {selectTable} = HydratedTableContext.useContext();

  const note = getNote();
  const table = selectTable(note);
  assert(table != null, `Table not found for ${tableName}`);

  const computedTable = getEvaluatedTable(tableName)(note) ?? [];

  const tableEditableProps: TableEditableProps = {
    ...TABLE_EDITABLE_DEFAULT_PROPS,
    table,
    computedTable,
    fileId: stepIdBeingEdited,
    tableNamespace,
    cid,
    multiEditing: true,
  };

  const tableCellEditorProps: TableCellEditorProps = {
    i: index[0],
    j: index[1],
    fileId: stepIdBeingEdited,
    tableName,
    tableNamespace,
    cid,
    getLastIndex: () => lastIndex ?? index,
    disableFormulas: false,
    cellEnterHandler: onCellEnter,
    cellEscapeHandler: () => {
      finishEditing();
    },
    storeState: () => {},
    commitCell: (index: TableIndex, text: string) => {
      // commitCell's signature is synchronous, so we can't await here. Explicitly catch to keep a
      // failed note update from surfacing as an unhandled promise rejection.
      updateCell({text}).catch((error) => {
        captureException(error);
      });
    },
    moveFromCell: () => {},
    keyDownHook: () => {},
    cellError,
    stepIdBeingDisplayed,
  };

  const documentEditorContextValue = useMemo(() => ({documentName: stepName}), [stepName]);

  return (
    <>
      <DocumentEditorContext.Provider value={documentEditorContextValue}>
        <TableModelProvider tableProps={tableEditableProps}>
          <RowModelProvider rowIndex={index[0]}>
            <TableCellEditor
              {...tableCellEditorProps}
              floating={{stepName, navigateToEditingCell: onReturnToStep}}
            />
          </RowModelProvider>
        </TableModelProvider>
      </DocumentEditorContext.Provider>
    </>
  );
}

export function CrossStepFormulaBox(props: CrossStepFormulaBoxProps): JSX.Element {
  return (
    <DocumentNoteProvider documentId={props.stepIdBeingEdited} tableNamespace={props.tableNamespace}>
      <HydratedTableProvider tableName={props.tableName}>
        <CrossStepFormulaBoxInner {...props} />
      </HydratedTableProvider>
    </DocumentNoteProvider>
  );
}

export interface ActiveCrossStepFormulaBoxProps {
  tableNamespace: string;
  stepIdBeingDisplayed: string | undefined;
  steps: Array<{documentId: string; name: string}>;
  onReturnToStep: (stepId: string) => void;
}

/**
 * Subscribes to the active cell edit in `tableNamespace` and renders the formula box when that edit is on a
 * step other than the one being displayed. Keeping the subscription here means only this component
 * re-renders when cell editing starts or stops.
 */
export function ActiveCrossStepFormulaBox({
  tableNamespace,
  stepIdBeingDisplayed,
  steps,
  onReturnToStep,
}: ActiveCrossStepFormulaBoxProps): JSX.Element | null {
  const selectActiveEdit = useCallback(
    (store: typeof SpreadsheetStore) => {
      const storeState = store.getState(tableNamespace);
      if (storeState.editingTable && storeState.fileId && storeState.index) {
        return {stepId: storeState.fileId, tableName: storeState.editingTable, index: storeState.index};
      }
      return null;
    },
    [tableNamespace]
  );
  const activeEdit = useStoreSelector(SpreadsheetStore, selectActiveEdit, isEqual);
  const editingStep = activeEdit ? steps.find((step) => step.documentId === activeEdit.stepId) : undefined;
  const editingStepId = editingStep?.documentId;

  const onReturnToEditingStep = useCallback(() => {
    if (editingStepId) {
      onReturnToStep(editingStepId);
    }
  }, [editingStepId, onReturnToStep]);

  if (!activeEdit || !editingStep || !stepIdBeingDisplayed || activeEdit.stepId === stepIdBeingDisplayed) {
    return null;
  }

  return (
    <CrossStepFormulaBox
      stepName={editingStep.name}
      stepIdBeingEdited={activeEdit.stepId}
      stepIdBeingDisplayed={stepIdBeingDisplayed}
      tableName={activeEdit.tableName}
      index={activeEdit.index}
      tableNamespace={tableNamespace}
      onReturnToStep={onReturnToEditingStep}
    />
  );
}
